"use client";

import {
	RiBookmarkFill,
	RiBookmarkLine,
	RiCheckLine,
	RiFilterLine,
	RiGroup2Fill,
	RiGroup2Line,
	RiLiveFill,
	RiLiveLine,
	RiSettings3Line,
	RiStarFill,
	RiStarLine,
} from "@remixicon/react";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@workspace/ui/components/dropdown-menu";
import { useCallback, useState } from "react";
import { cn } from "@/lib/utils";
import { AdvancedSettingsModal } from "../advanced-settings-modal";

export type BookmarkFilterType =
	| "all"
	| "saved"
	| "rated"
	| "following"
	| "stream";

export interface BookmarkFilters {
	saved: boolean;
	rated: boolean;
	following: boolean;
	stream: boolean;
}

// Helper functions to convert between filter systems
export function bookmarkFiltersToSingle(
	filters: BookmarkFilters,
): BookmarkFilterType {
	if (filters.saved) return "saved";
	if (filters.rated) return "rated";
	if (filters.following) return "following";
	if (filters.stream) return "stream";
	return "all";
}

export function singleToBookmarkFilters(
	filter: BookmarkFilterType,
): BookmarkFilters {
	return {
		saved: filter === "saved",
		rated: filter === "rated",
		following: filter === "following",
		stream: filter === "stream",
	};
}

export interface BookmarkFilterProps {
	filters: BookmarkFilters;
	onFiltersChange: (filters: BookmarkFilters) => void;
	className?: string;
	title?: string;
	hideFollowing?: boolean;
	calendarPreferences?: import("@workspace/types/src/entities/user").CalendarViewPreferences;
	onCalendarPreferencesSaved?: (
		preferences: import("@workspace/types/src/entities/user").CalendarViewPreferences,
	) => void;
}

export const BookmarkFilter = function BookmarkFilter({
	filters,
	onFiltersChange,
	className,
	title = "Filter sets",
	hideFollowing = false,
	calendarPreferences,
	onCalendarPreferencesSaved,
}: BookmarkFilterProps) {
	// Modal state
	const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

	// Determine current active filter (single selector behavior)
	const currentFilter = bookmarkFiltersToSingle(filters);

	const getButtonIcon = () => {
		switch (currentFilter) {
			case "saved":
				return <RiBookmarkFill className="h-4 w-4" />;
			case "rated":
				return <RiStarFill className="h-4 w-4" />;
			case "following":
				return <RiGroup2Fill className="h-4 w-4" />;
			case "stream":
				return <RiLiveFill className="h-4 w-4" />;
			default:
				return <RiFilterLine className="h-4 w-4" />;
		}
	};

	const handleFilterSelect = useCallback(
		(newFilter: BookmarkFilterType) => {
			// If clicking the same filter, toggle it off (go back to 'all')
			const targetFilter = newFilter === currentFilter ? "all" : newFilter;
			onFiltersChange(singleToBookmarkFilters(targetFilter));
		},
		[currentFilter, onFiltersChange],
	);

	return (
		<>
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<button
						type="button"
						className={cn(
							"relative flex h-9 w-9 items-center justify-center rounded-xl transition-colors active:scale-[0.99]",
							currentFilter !== "all"
								? "bg-primary text-primary-foreground"
								: "border border-input bg-background text-foreground hover:bg-accent",
							className,
						)}
						title={title}
						aria-label={title}
					>
						{getButtonIcon()}
					</button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end" sideOffset={8} className="w-40 rounded-2xl">
					<DropdownMenuItem
						onClick={() => handleFilterSelect("all")}
						className="justify-between rounded-t-xl rounded-b-sm"
					>
						<span className="flex items-center gap-2">
							<RiFilterLine className="h-4 w-4" />
							All
						</span>
						{currentFilter === "all" && <RiCheckLine className="h-4 w-4" />}
					</DropdownMenuItem>
					<DropdownMenuItem
						onClick={() => handleFilterSelect("saved")}
						className="justify-between rounded-sm"
					>
						<span className="flex items-center gap-2">
							<RiBookmarkLine className="h-4 w-4" />
							Saved
						</span>
						{currentFilter === "saved" && <RiCheckLine className="h-4 w-4" />}
					</DropdownMenuItem>
					<DropdownMenuItem
						onClick={() => handleFilterSelect("rated")}
						className="justify-between rounded-sm"
					>
						<span className="flex items-center gap-2">
							<RiStarLine className="h-4 w-4" />
							Rated
						</span>
						{currentFilter === "rated" && <RiCheckLine className="h-4 w-4" />}
					</DropdownMenuItem>
					{!hideFollowing && (
						<DropdownMenuItem
							onClick={() => handleFilterSelect("following")}
							className="justify-between rounded-sm"
						>
							<span className="flex items-center gap-2">
								<RiGroup2Line className="h-4 w-4" />
								Following
							</span>
							{currentFilter === "following" && (
								<RiCheckLine className="h-4 w-4" />
							)}
						</DropdownMenuItem>
					)}
					<DropdownMenuItem
						onClick={() => handleFilterSelect("stream")}
						className="justify-between rounded-sm"
					>
						<span className="flex items-center gap-2">
							<RiLiveLine className="h-4 w-4" />
							Stream
						</span>
						{currentFilter === "stream" && <RiCheckLine className="h-4 w-4" />}
					</DropdownMenuItem>
					<DropdownMenuSeparator />
					<DropdownMenuItem
						onClick={() => setShowAdvancedSettings(true)}
						className="justify-between rounded-b-xl rounded-t-sm"
					>
						<span className="flex items-center gap-2">
							<RiSettings3Line className="h-4 w-4" />
							Settings
						</span>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>

			{/* Advanced Settings Modal */}
			<AdvancedSettingsModal
				isOpen={showAdvancedSettings}
				onClose={() => setShowAdvancedSettings(false)}
				initialPreferences={calendarPreferences}
				onPreferencesSaved={onCalendarPreferencesSaved}
			/>
		</>
	);
};

BookmarkFilter.displayName = "BookmarkFilter";
